<template>
  <div class="container-fluid">
    <div class="row">
      <div :class="`col-sm-${getColumn()} items-col`">
        <draggable group="hooks" draggable=".draggable" v-model="computedHooks">
          <div v-for="question in computedHooks" :key="question.id" class="hook-item draggable">
            <hook-media :hook="question.mediaClip"></hook-media>
            <div class="options pull-right">
              <el-tooltip content="Remove">
                <i class="fa fa-fw fa-trash" @click="handleRemoveHook(question.id)"></i>
              </el-tooltip>
            </div>
          </div>
          <div slot="footer">
            <div class="placeholder-container hook-placeholder-container">Drag hook</div>
          </div>
        </draggable>
        <div style="margin-top: 65px">
          <el-checkbox v-model="computedIsRandomized">Present in random order</el-checkbox>
        </div>
      </div>

      <div class="col-sm-8 items-col" v-if="computedHooks.length">
        <draggable group="music-questions" draggable=".draggable" v-model="computedMatrixChoices">
          <div
            v-for="matrixChoice in computedMatrixChoices"
            :key="matrixChoice.title"
            class="question-item draggable"
          >
            <div>
              <div class="title">{{ matrixChoice.title }}</div>
              <div
                v-for="choice in matrixChoice.choices"
                :key="choice.id"
                class="choice"
              >{{ choice.title }}</div>
            </div>
            <div class="options pull-right">
              <span v-if="computedMatrixChoices.length > 1">
                <el-tooltip content="Conditional">
                  <i
                    class="fa fa-fw fa-link"
                    :class="{'has-conditional': isConditional(matrixChoice)}"
                    @click="handleSetConditional(matrixChoice)"
                  ></i>
                </el-tooltip>

                <span class="tool-divider"></span>
              </span>

              <el-tooltip content="Remove">
                <i class="fa fa-fw fa-trash" @click="handleRemoveMatrixChoice(matrixChoice.id)"></i>
              </el-tooltip>
            </div>
          </div>
          <div slot="footer">
            <div
              class="placeholder-container media-question-placeholder-container"
            >Drag media question</div>
          </div>
        </draggable>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex';
import draggable from 'vuedraggable';

import * as surveyBuilderTypes from '@/store/modules/surveybuilder/mutation-types';

import HookMedia from './HookMedia';

export default {
  name: 'MusicEditorExpanded',
  components: {
    draggable,
    HookMedia,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    isEditable: {
      type: Boolean,
      default: true,
    },
  },
  data: () => ({
    isRandomized: false,
    conditionalVisibile: false,
  }),
  computed: {
    ...mapState('surveybuilder', ['survey']),

    computedValue: {
      get() {
        return this.value;
      },
      set(value) {
        this.$emit('input', value);
      },
    },

    computedHooks: {
      get() {
        return this.value.group;
      },
      set(value) {
        this.computedValue = { ...this.computedValue, group: value };
      },
    },

    computedMatrixChoices: {
      get() {
        return this.value.matrixChoices;
      },
      set(value) {
        this.computedValue = { ...this.computedValue, matrixChoices: value };
      },
    },

    computedIsRandomized: {
      get() {
        return this.isRandomized;
      },
      set(value) {
        // Optimistic update
        this.isRandomized = value;

        this.$store.dispatch('surveybuilder/surveyUpdateConfig', {
          ...this.survey.config,
          randomize_media: value,
        });
      },
    },
  },
  methods: {
    isConditional(matrixChoice) {
      return matrixChoice.visibleIf && matrixChoice.visibleIf.trim() !== '';
    },

    handleRemoveHook(hookId) {
      this.computedHooks = this.computedHooks.filter(h => h.id !== hookId);
    },

    handleRemoveMatrixChoice(matrixChoiceId) {
      this.computedMatrixChoices = this.computedMatrixChoices.filter(m => m.id !== matrixChoiceId);
    },

    handleSetConditional(matrixChoice) {
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_MATRIX_CONDITIONAL_LOGIC_DIALOG_MATRIX_CHOICE}`,
        matrixChoice,
      );
      this.$store.commit(
        `surveybuilder/${surveyBuilderTypes.SET_MATRIX_CONDITIONAL_LOGIC_DIALOG_VISIBILITY}`,
        true,
      );
    },
    getColumn() {
      return this.computedHooks.length ? 4 : 12;
    },
  },
  watch: {
    survey() {
      this.isRandomized = this.survey.config.randomize_media || false;
    },
  },
};
</script>

<style lang="scss" scoped>
@import '../../../../assets/style/_variables_live.scss';

.items-col {
  border-right: 1px solid $colony-light-gray;

  .options {
    margin-top: -15px;
    color: darken($colony-light-gray, 15%);
  }

  .options i {
    cursor: pointer;
  }
}

.hook-item {
  width: 100%;
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 3px;
  background-color: $colony-light-gray;
  cursor: grab;

  .play {
    font-size: 2em;
    color: darken($colony-light-gray, 15%);
    cursor: pointer;
  }

  .detail {
    display: inline-block;
    margin-left: 1em;
  }

  .title {
    font-weight: bold;
  }
}

.question-item {
  width: 100%;
  margin-bottom: 4px;
  padding: 8px;
  border-radius: 3px;
  background-color: lighten($colony-light-gray, 5%);
  cursor: grab;

  .title {
    font-size: 1.2em;
    margin-bottom: 0.4em;
  }

  .choice {
    padding: 2px 4px;
    font-size: 0.8em;
    margin: 2px 4px;
    background-color: white;
    border: 1px solid darken($colony-light-gray, 15%);
    cursor: default;
    white-space: nowrap;
    display: inline-block;
  }

  .tool-divider {
    display: inline-block;
    width: 1px;
    height: 1em;
    margin: 0 8px;
    vertical-align: middle;
    position: relative;
    background-color: darken($colony-light-gray, 15%);
  }

  .has-conditional {
    color: $colony-success;
  }
}

.placeholder-container {
  position: absolute;
  border: 2px dashed #ccc;
  display: flex;
  align-items: center;
  justify-content: center;
  text-shadow: 2px 2px 2px #efefef;
  font-size: 0.8em;
  color: #ccc;
  margin: 0;
  width: calc(100% - 30px);
  z-index: 0;
}
.hook-placeholder-container {
  min-height: 55px;
}
.media-question-placeholder-container {
  min-height: 38px;
}
</style>



// WEBPACK FOOTER //
// MusicEditorExpanded.vue?30c1fb50